import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import theme from '../../theme';
import * as groceryApi from '../grocery/groceryApi';

var Page = styled.div`
  padding: 16px;
  max-width: 900px;
  margin: 0 auto;
  color: ${theme.text};
`;

var Controls = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
`;

var Select = styled.select`
  padding: 12px;
  border: 2px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: ${theme.bg};
  color: #fff;
  font-size: 0.9rem;
  min-height: 48px;
  &:focus { outline: none; border-color: ${theme.accent}; }
`;

var GenBtn = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: ${theme.radiusSm};
  background: ${theme.accentGradient};
  color: #000;
  font-weight: 700;
  cursor: pointer;
  min-height: 48px;
  font-size: 1rem;
  &:hover { transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: default; transform: none; }
`;

var Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  @media (min-width: 600px) { grid-template-columns: 1fr 1fr; }
`;

var Card = styled.div`
  background: ${theme.bgCard};
  border: 1px solid ${theme.border};
  border-radius: ${theme.radius};
  padding: 20px;
  &:hover { border-color: ${theme.borderLight}; }
`;

var CardTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #fff;
  font-size: 1.1rem;
`;

var CategoryBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: ${theme.radiusPill};
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  background: ${p => p.$color ? p.$color + '22' : 'rgba(255,255,255,0.1)'};
  color: ${p => p.$color || theme.textMuted};
  border: 1px solid ${p => p.$color ? p.$color + '44' : theme.border};
  margin-bottom: 8px;
`;

var Meta = styled.div`
  display: flex;
  gap: 14px;
  margin: 8px 0;
  font-size: 0.8rem;
  color: ${theme.textMuted};
`;

var Desc = styled.p`
  color: ${theme.textMuted};
  font-size: 0.85rem;
  margin: 8px 0;
  line-height: 1.4;
`;

var IngList = styled.div`
  margin: 8px 0;
  padding: 10px;
  background: rgba(0,0,0,0.2);
  border-radius: ${theme.radiusSm};
  max-height: 140px;
  overflow-y: auto;
`;

var IngItem = styled.div`
  font-size: 0.8rem;
  color: ${theme.text};
  padding: 2px 0;
`;

var IngTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${theme.accent};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

var AddToListBtn = styled.button`
  padding: 8px 14px;
  border: 1px solid ${theme.accent};
  border-radius: ${theme.radiusSm};
  background: transparent;
  color: ${theme.accent};
  cursor: pointer;
  font-size: 0.8rem;
  margin-top: 10px;
  &:hover { background: rgba(74,222,128,0.1); }
  &:disabled { opacity: 0.4; cursor: default; }
`;

var Empty = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${theme.textDim};
  font-size: 1.1rem;
`;

var CATEGORY_COLORS = {
  breakfast: theme.breakfast,
  lunch: theme.lunch,
  dinner: theme.dinner,
  snack: theme.snack,
  dessert: theme.dessert,
  side: theme.side
};

var MealIdeasPage = function ({ onLogout }) {
  var [category, setCategory] = useState('all');
  var [count, setCount] = useState(4);
  var [ideas, setIdeas] = useState([]);
  var [loading, setLoading] = useState(false);
  var [addedSet, setAddedSet] = useState({});

  var generate = useCallback(async function () {
    setLoading(true);
    setAddedSet({});
    try {
      var url = '/api/meal-ideas?count=' + count;
      if (category !== 'all') url += '&category=' + category;
      var res = await fetch(url, { credentials: 'include' });
      if (res.status === 401) { onLogout(); return; }
      var data = await res.json();
      setIdeas(data);
    } catch (err) {}
    setLoading(false);
  }, [category, count, onLogout]);

  var addToGrocery = async function (recipe) {
    if (!recipe.ingredients?.length) return;
    try {
      var items = recipe.ingredients.map(function (ing) {
        return { name: ing.name, amount: ing.amount, unit: ing.unit, category: ing.category || 'other', recipeSource: recipe.title };
      });
      await groceryApi.addItemsBulk(items);
      setAddedSet(function (prev) { var n = {}; n[recipe._id] = true; return Object.assign({}, prev, n); });
    } catch (err) {}
  };

  return (
    <Page>
      <Controls>
        <Select value={category} onChange={function (e) { setCategory(e.target.value); }}>
          <option value="all">Any category</option>
          <option value="breakfast">Breakfast</option>
          <option value="lunch">Lunch</option>
          <option value="dinner">Dinner</option>
          <option value="snack">Snack</option>
          <option value="side">Side</option>
        </Select>
        <Select value={count} onChange={function (e) { setCount(parseInt(e.target.value)); }}>
          <option value="2">2 ideas</option>
          <option value="4">4 ideas</option>
          <option value="6">6 ideas</option>
          <option value="8">8 ideas</option>
        </Select>
        <GenBtn onClick={generate} disabled={loading}>
          {loading ? 'Shuffling...' : '🎲 Get Ideas'}
        </GenBtn>
      </Controls>

      {ideas.length === 0 ? (
        <Empty>{loading ? 'Loading...' : 'Tap "Get Ideas" to see random recipe suggestions.'}</Empty>
      ) : (
        <Grid>
          {ideas.map(function (recipe) {
            var color = CATEGORY_COLORS[recipe.category] || theme.accent;
            var totalTime = (recipe.prepTime || 0) + (recipe.cookTime || 0);
            return (
              <Card key={recipe._id}>
                <CardTitle>{recipe.title}</CardTitle>
                <CategoryBadge $color={color}>{recipe.category}</CategoryBadge>
                <Meta>
                  {recipe.servings > 0 && <span>🍽️ {recipe.servings}</span>}
                  {totalTime > 0 && <span>⏰ {totalTime}m</span>}
                </Meta>
                {recipe.description && <Desc>{recipe.description}</Desc>}
                {recipe.ingredients?.length > 0 && (
                  <IngList>
                    <IngTitle>Ingredients ({recipe.ingredients.length})</IngTitle>
                    {recipe.ingredients.map(function (ing, i) {
                      return <IngItem key={i}>{ing.amount} {ing.unit} {ing.name}</IngItem>;
                    })}
                  </IngList>
                )}
                <AddToListBtn onClick={function () { addToGrocery(recipe); }}
                  disabled={!!addedSet[recipe._id] || !recipe.ingredients?.length}>
                  {addedSet[recipe._id] ? '✓ Added to list' : '🛒 Add to grocery list'}
                </AddToListBtn>
              </Card>
            );
          })}
        </Grid>
      )}
    </Page>
  );
};

export default MealIdeasPage;
